import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { Link } from 'react-router';
import { useHypoteseGrunnlag, useLenkGrunnlag, type HypothesisEvidenceItem } from '../api/useHypoteseGrunnlag.ts';
import type { AdminUserRef } from '../api/useHypoteseBehandling.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { useSaker, type SubmissionSummary } from '../api/useSubmissions.ts';
import { saksnr } from '../innboks/saksnummer.ts';
import { Velger } from '../skall/Velger.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { statusEtikett } from '../status/status.ts';
import { Tekstknapp, Varsel } from './deler.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { hvem, navnAv } from './lesning.ts';
import { kortDatoAv } from './setning.ts';

/**
 * H3.1 — grunnlaget og notisen på hypotesen (konseptsiden del C og D).
 *
 *   Grunnlag                                   Legg til saker … L
 *   37  innspill fra brukere
 *   41 saker lenket · 3 anonyme teller ikke · sist lagt til 2. nov av Ingrid Moe
 *   [sakene som rader — for den som har tilgang til dem]
 *
 * Tallet er seksjonens helt (DD-67: beslutningsgrunnlag i `display`), rått og uten farge (Kh8).
 * En bidragsyter får tallet og ingenting mer — ingen liste, ingen knapp og ingen setning om det
 * hun ikke får se (Kh7; ux-flow: ukjent tilstand er ikke en forklaring hun trenger).
 */

const MAKS_RADER = 5;
const NOTIS_MAKS = 280;

/** Sporets ikon og navn (DD-61): leses før teksten, på raden og i velgeren. */
export const sporAv = (type: SubmissionSummary['type']) => (type === 'feil' ? { ikon: 'bug-outline', navn: 'Feil' } : { ikon: 'bulb-outline', navn: 'Ønske' });

/** «4 har spurt», eller «anonym» — en anonym sak teller aldri i Reach, men den finnes (DD-63). */
const reachLedd = (s: SubmissionSummary) => (s.anonymous && s.reach === 0 ? 'anonym' : `${s.reach} har spurt`);

/** «41 saker lenket · 3 anonyme teller ikke · sist lagt til 2. nov av Ingrid Moe». */
function metaLinje(antall: number, anonyme: number, siste: { av: AdminUserRef; da: string } | null, megId: string | undefined): string {
  const deler = [`${antall} ${antall === 1 ? 'sak' : 'saker'} lenket`];
  if (anonyme > 0) deler.push(`${anonyme} ${anonyme === 1 ? 'anonym' : 'anonyme'} teller ikke`);
  if (siste) deler.push(`sist lagt til ${kortDatoAv(siste.da)} av ${hvem(siste.av, megId, 'deg')}`);
  return deler.join(' · ');
}

export interface GrunnlagProps {
  h: Hypothesis;
  megId: string | undefined;
  /** Overskriftsnivået i detaljen: h2 under 900 px (tittelen er h1), ellers h3. */
  tittelTag: 'h2' | 'h3';
  /** Behandler i Innspill OG en rolle i Hypoteser, og grunnlaget er ikke låst av notisen. */
  kanLenke: boolean;
  /** Saken som venter på å bli tatt ut (angre-vinduet) — raden står ikke så lenge. */
  fjernes: string | null;
  onFjern: (sak: SubmissionSummary) => void;
  onKvittering: (tekst: string) => void;
  onFeil: (e: unknown) => void;
  velgerAapen: boolean;
  setVelgerAapen: (v: boolean) => void;
}

/**
 * Seksjonen Grunnlag (skjerm 7–10, 15). Grunnlaget hentes for seg: hypotesen står med én gang,
 * og lista får sitt eget skjelett. «Legg til saker …» finnes ikke før tallet er der — ukjent
 * tilstand er strengest.
 */
export function GrunnlagSeksjon(p: GrunnlagProps) {
  const { h, megId, kanLenke } = p;
  const smal = useSmal();
  const grunnlag = useHypoteseGrunnlag(h.id);
  const [alle, setAlle] = useState(false);
  const knapp = useRef<HTMLButtonElement>(null);
  const Tittel = p.tittelTag;
  const tittelId = `hb-grunnlag-h-${h.id}`;
  const data = grunnlag.data;
  const kanLeggeTil = kanLenke && data !== undefined;

  // L åpner velgeren (G er skallets akkord for sidene). Aldri mens et felt har fokus.
  const aapen = useRef(p.setVelgerAapen);
  aapen.current = p.setVelgerAapen;
  useEffect(() => {
    if (!kanLeggeTil) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || e.key !== 'l') return;
      e.preventDefault();
      aapen.current(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kanLeggeTil]);

  const items = data?.items;
  const synlige = (items ?? []).filter((x) => x.submission.id !== p.fjernes);
  const vis = alle ? synlige : synlige.slice(0, MAKS_RADER);
  const siste = items?.[0] ? { av: items[0].linkedBy, da: items[0].linkedAt } : null;

  return (
    <section className="hb-sek hb-grunnlag" aria-labelledby={tittelId} data-testid="hb-grunnlag">
      <div className="hb-sek-hode">
        <Tittel id={tittelId} className="hb-sek-tittel">
          Grunnlag
        </Tittel>
        {grunnlag.isPending ? <span className="hb-dempet hb-liten">Henter …</span> : null}
        {kanLeggeTil ? (
          <button ref={knapp} type="button" className="panel-knapp-lenke hb-sek-lenke" aria-haspopup="dialog" aria-expanded={p.velgerAapen} aria-keyshortcuts="l" onClick={() => p.setVelgerAapen(!p.velgerAapen)} data-testid="hb-grunnlag-legg-til">
            {smal ? 'Legg til …' : 'Legg til saker …'}
            {smal ? null : <kbd>L</kbd>}
          </button>
        ) : null}
      </div>
      {grunnlag.isPending ? (
        <div role="status" aria-label="Henter grunnlaget" className="hb-grunnlag-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          {[0, 1, 2].map((i) => (
            <span key={i} className="panel-skjelett-linje" />
          ))}
        </div>
      ) : grunnlag.isError || !data ? (
        <Varsel tone="info" handling={<Tekstknapp onClick={() => void grunnlag.refetch()}>Prøv igjen</Tekstknapp>}>
          Grunnlaget kunne ikke hentes.
        </Varsel>
      ) : data.count === 0 ? (
        <div className="hb-notis-tom" data-testid="hb-grunnlag-tom">
          <p>Ingen saker er lagt til ennå.</p>
          {kanLenke ? <p className="hb-dempet">Legg til saker fra Innspill som viser hva brukerne har meldt om dette.</p> : null}
        </div>
      ) : (
        <>
          <p className="hb-grunnlag-helt">
            <span className="hb-grunnlag-tall" data-testid="hb-grunnlag-tall">
              {data.reach}
            </span>
            <span className="hb-grunnlag-etikett">innspill fra brukere</span>
            <span className="hb-grunnlag-meta" data-testid="hb-grunnlag-meta">
              {items ? metaLinje(data.count, data.anonymousCount, siste, megId) : `fra ${data.count} ${data.count === 1 ? 'sak' : 'saker'} i Innspill`}
            </span>
          </p>
          {items ? (
            <>
              <ul className="hb-grunnlag-liste" aria-label="Sakene i grunnlaget">
                {vis.map((x) => (
                  <GrunnlagRad key={x.submission.id} x={x} megId={megId} smal={smal} kanFjerne={kanLenke && !smal} onFjern={() => p.onFjern(x.submission)} />
                ))}
              </ul>
              {!alle && synlige.length > MAKS_RADER ? (
                <div className="hb-grunnlag-alle">
                  <Tekstknapp onClick={() => setAlle(true)}>{`Vis alle ${synlige.length}`}</Tekstknapp>
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}
      {p.velgerAapen && kanLeggeTil ? <SakVelger h={h} utloeser={knapp} lenket={new Set((items ?? []).map((x) => x.submission.id))} onLukk={() => p.setVelgerAapen(false)} onKvittering={p.onKvittering} onFeil={p.onFeil} /> : null}
    </section>
  );
}

/**
 * Én sak i grunnlaget: sporets ikon, teksten på én linje i `body`, og én meta-linje i `small` —
 * spor, nummer, sakens EGEN status (lenkingen endrer den ikke), sakens tall og hvem som la den til.
 * «Åpne» og «Fjern» kommer ved peker eller fokus på PC; under 900 px er raden lenken til saken (P6).
 */
function GrunnlagRad({ x, megId, smal, kanFjerne, onFjern }: { x: HypothesisEvidenceItem; megId: string | undefined; smal: boolean; kanFjerne: boolean; onFjern: () => void }) {
  const s = x.submission;
  const spor = sporAv(s.type);
  const nr = saksnr(s.number);
  const til = `/saker?nr=${s.number}`;
  const innhold = (
    <>
      <Ikon navn={spor.ikon} className="hb-grunnlag-ikon" />
      <span className="hb-grunnlag-tekst">{s.text}</span>
      <span className="hb-grunnlag-rad-meta">
        <b>{spor.navn}</b>
        {` · ${[nr, statusEtikett(s.status, s.type), reachLedd(s), ...(smal ? [] : [`${hvem(x.linkedBy, megId, 'du')} la til ${kortDatoAv(x.linkedAt)}`])].join(' · ')}`}
      </span>
    </>
  );
  if (smal) {
    return (
      <li>
        <Link to={til} className="hb-grunnlag-rad hb-grunnlag-rad-lenke" data-testid={`hb-grunnlag-rad-${s.number}`}>
          {innhold}
        </Link>
      </li>
    );
  }
  return (
    <li className="hb-grunnlag-rad" data-testid={`hb-grunnlag-rad-${s.number}`}>
      {innhold}
      <span className="hb-grunnlag-k">
        <Link to={til} className="panel-knapp-lenke" aria-label={`Åpne ${nr}`}>
          Åpne
        </Link>
        {kanFjerne ? (
          <button type="button" className="panel-knapp-lenke hb-noeytral" aria-label={`Fjern ${nr} fra grunnlaget`} onClick={onFjern}>
            Fjern
          </button>
        ) : null}
      </span>
    </li>
  );
}

/**
 * «Legg til saker i grunnlaget» (skjerm 7b): samme velger som på saken, speilvendt — søk i saker,
 * avkrysning per rad (flere saker er det normale her), og «Legg til N» som eneste fylte knapp.
 * Saker som alt er i grunnlaget står avkrysset og dempet; de tas ut på raden under Grunnlag.
 * Søket treffer tekst og nummer i de nyeste sakene. Space krysser av, Ctrl+↵ legger til.
 */
function SakVelger({ h, utloeser, lenket, onLukk, onKvittering, onFeil }: { h: Hypothesis; utloeser: RefObject<HTMLElement | null>; lenket: ReadonlySet<string>; onLukk: () => void; onKvittering: (t: string) => void; onFeil: (e: unknown) => void }) {
  const saker = useSaker({ sort: 'nyest' });
  const smal = useSmal();
  const lenk = useLenkGrunnlag();
  const [sok, setSok] = useState('');
  const [valgt, setValgt] = useState<string[]>([]);
  const alle = useMemo(() => saker.data?.pages.flatMap((p) => p.items) ?? [], [saker.data]);
  const q = sok.trim().toLowerCase().replace(/^#/, '');
  const treff = alle.filter((s) => !q || s.text.toLowerCase().includes(q) || String(s.number).includes(q));
  const grunn = valgt.length === 0 ? 'Kryss av minst én sak.' : lenk.isPending ? 'Legger til …' : null;

  async function leggTil() {
    if (grunn) return;
    try {
      for (const id of valgt) await lenk.mutateAsync({ hypoteseId: h.id, sakId: id });
      onLukk();
      onKvittering(`${valgt.length === 1 ? '1 sak er lagt' : `${valgt.length} saker er lagt`} til i grunnlaget for ${nummerTekst(h.number)}.`);
    } catch (e) {
      onFeil(e);
    }
  }

  const tast = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void leggTil();
    }
  };

  return (
    <Velger
      tittel="Legg til saker i grunnlaget"
      tittelId={`hb-sakvelger-h-${h.id}`}
      utloeser={utloeser}
      onLukk={onLukk}
      onKeyDown={tast}
      testid="hb-sakvelger"
      fot={
        <>
          {/* Grunnen står der antallet ellers står — ved knappen, aldri en stum lås. */}
          <span id="hb-sakvelger-las" className="panel-velger-las">
            {valgt.length > 0 ? `${valgt.length} valgt` : grunn}
          </span>
          <button type="button" className="panel-knapp" aria-disabled={grunn ? true : undefined} aria-describedby={grunn ? 'hb-sakvelger-las' : undefined} aria-busy={lenk.isPending || undefined} aria-keyshortcuts="Control+Enter" onClick={grunn ? undefined : () => void leggTil()} data-testid="velger-legg-til">
            {valgt.length > 0 ? `Legg til ${valgt.length}` : 'Legg til'}
            {smal ? null : <kbd>Ctrl ↵</kbd>}
          </button>
        </>
      }
    >
      <label className="panel-velger-sok">
        <Ikon navn="search-outline" />
        <input type="text" value={sok} onChange={(e) => setSok(e.target.value)} aria-label="Søk i saker" placeholder="Tekst eller nummer" autoComplete="off" />
        {smal ? null : <kbd aria-hidden="true">Esc</kbd>}
      </label>
      {saker.isPending ? (
        <p className="hb-dempet hb-liten" role="status">
          Henter sakene …
        </p>
      ) : saker.isError ? (
        <Varsel tone="info" handling={<Tekstknapp onClick={() => void saker.refetch()}>Prøv igjen</Tekstknapp>}>
          Sakene kunne ikke hentes.
        </Varsel>
      ) : treff.length === 0 ? (
        <p className="hb-dempet">{q ? `Ingen saker matcher «${sok.trim()}».` : 'Ingen saker ennå.'}</p>
      ) : (
        <ul className="panel-velger-liste" aria-label="Saker">
          {treff.map((s) => {
            const alt = lenket.has(s.id);
            const paa = alt || valgt.includes(s.id);
            const spor = sporAv(s.type);
            return (
              <li key={s.id}>
                <label className={`panel-velger-rad${alt ? ' panel-velger-rad-dempet' : ''}`}>
                  <input type="checkbox" checked={paa} disabled={alt} onChange={() => setValgt((v) => (v.includes(s.id) ? v.filter((x) => x !== s.id) : [...v, s.id]))} />
                  <span className="panel-velger-tittel">{s.text}</span>
                  <span className="panel-velger-hoyre">{alt ? 'alt i grunnlaget' : ''}</span>
                  <span className="panel-velger-meta">
                    {spor.navn} · {saksnr(s.number)} · {statusEtikett(s.status, s.type)} · {reachLedd(s)}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Velger>
  );
}

/** Dagens dato slik sluttbrukeren ser den under notisen: «8. desember». */
const langDato = (iso: string) => new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long', timeZone: 'Europe/Oslo' }).format(new Date(iso));
const klokke = (iso: string) => new Date(iso).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' });

/**
 * Notisen i den formen sluttbrukeren ser den (widget, portal og `@innspel/web`): en hevet flate med
 * egen etikett og dato — en annen FORM enn svaret, ikke bare en annen farge (DD-27). Teksten
 * rendres som tekst.
 */
export function Notisflate({ tekst, dato }: { tekst: string; dato: string }) {
  return (
    <div className="hb-notisflate">
      <span className="hb-notisflate-etikett">
        <Ikon navn="megaphone-outline" />
        Hva vi fant ut · {langDato(dato)}
      </span>
      {tekst.trim() ? <p className="hb-notisflate-tekst">{tekst}</p> : <p className="hb-notisflate-tekst hb-dempet">Teksten din står her.</p>}
    </div>
  );
}

/** Hvem som skriver notisen: eieren, eller — stoppet uten eier — beslutningstakeren som stoppet. */
export function notisSkriver(h: Hypothesis): AdminUserRef | undefined {
  if (h.owner) return h.owner;
  if (h.status !== 'stoppet') return undefined;
  return [...h.transitions].reverse().find((t) => t.toStatus === 'stoppet')?.by;
}

/** Feltet er åpent for den innloggede: hun skriver, og ingenting er avgjort ennå, og noen kan få den. */
export const notisSkrivesAv = (h: Hypothesis, megId: string | undefined): boolean =>
  (h.status === 'konkludert' || h.status === 'stoppet') && !h.conclusionNotice && !h.conclusionNoticeSkipped && h.evidenceReach >= 1 && megId !== undefined && notisSkriver(h)?.id === megId;

/**
 * «Til de 37 som meldte fra» (skjerm 11–14, 16): under konklusjonen eller stoppet. Fire former —
 * eieren skriver; andre ser én setning; sendt er kvitteringen (fryst, som konklusjonen); «Ingen
 * notis» er eierens lagrede valg (P3). Uten noen å sende til er det ingen knapp å sende med.
 */
export function NotisSeksjon({
  h,
  megId,
  tenantName,
  tittelTag,
  tekst,
  setTekst,
  kanLenke,
  onLeggTil,
}: {
  h: Hypothesis;
  megId: string | undefined;
  tenantName: string | undefined;
  tittelTag: 'h2' | 'h3';
  tekst: string;
  setTekst: (t: string) => void;
  kanLenke: boolean;
  onLeggTil: () => void;
}) {
  const Tittel = tittelTag;
  const tittelId = `hb-notis-h-${h.id}`;
  const notis = h.conclusionNotice;
  const n = notis ? notis.recipientCount : h.evidenceReach;
  const tittel = n > 0 ? `Til de ${n} som meldte fra` : 'Til brukerne som meldte fra';
  const skriver = notisSkriver(h);
  const iDag = new Date().toISOString();
  const lengde = tekst.length;
  const teller = lengde >= NOTIS_MAKS ? `${lengde} av ${NOTIS_MAKS} tegn — fullt` : lengde >= NOTIS_MAKS * 0.8 ? `${lengde} av ${NOTIS_MAKS} tegn` : null;

  let innhold;
  let kant = null;
  if (notis) {
    kant = (
      <span className="hb-dempet hb-liten" data-testid="hb-notis-sendt">
        {`sendt ${kortDatoAv(notis.sentAt)} ${klokke(notis.sentAt)} av ${hvem(notis.by, megId, 'deg')}`}
      </span>
    );
    innhold = (
      <>
        <div className="hb-notis-ramme" data-testid="hb-notis-forhandsvisning">
          <Notisflate tekst={notis.text} dato={notis.sentAt} />
        </div>
        <p className="hb-dempet">{`Står under innspillene til de ${notis.recipientCount} i «Mine innspill». ${tenantName ?? 'Leietakeren'} har fått hendelsen med pseudonymene og varsler i egen kanal.`}</p>
      </>
    );
  } else if (h.conclusionNoticeSkipped) {
    innhold = (
      <p className="hb-dempet" data-testid="hb-notis-ingen">
        {`Ingen notis sendt (valgt av ${hvem(h.conclusionNoticeSkipped.by, megId, 'deg')}, ${kortDatoAv(h.conclusionNoticeSkipped.at)}).`}
      </p>
    );
  } else if (h.evidenceReach < 1) {
    innhold = (
      <div className="hb-notis-tom">
        <p>{h.evidenceCount > 0 ? 'Sakene i grunnlaget er anonyme, så det finnes ingen å sende en notis til.' : 'Ingen innspill er lenket til hypotesen, så det finnes ingen å sende en notis til.'}</p>
        {kanLenke ? (
          <>
            <p className="hb-dempet">Saker i Innspill kan fortsatt legges til som grunnlag — notisen går til dem som er lenket når den sendes.</p>
            <div>
              <Tekstknapp onClick={onLeggTil}>Legg til saker …</Tekstknapp>
            </div>
          </>
        ) : null}
      </div>
    );
  } else if (notisSkrivesAv(h, megId)) {
    innhold = (
      <>
        <p className="hb-dempet">Brukerne ser bare dette — ikke navn på ansatte, interne tall eller hva dere skal lansere.</p>
        <label className="hb-notis-felt">
          <span className="hb-notis-felt-hode">
            <b>Notis</b>
            {teller ? (
              <span role="status" aria-live="polite" className="hb-liten hb-dempet" data-testid="hb-notis-teller">
                {teller}
              </span>
            ) : null}
          </span>
          <textarea className="h-input h-tekstfelt" rows={4} maxLength={NOTIS_MAKS} value={tekst} onChange={(e) => setTekst(e.target.value)} />
        </label>
        <div className="hb-notis-forh">
          <span className="hb-notis-forh-etikett">Slik står det under hvert innspill</span>
          <div className="hb-notis-ramme" data-testid="hb-notis-forhandsvisning">
            <Notisflate tekst={tekst} dato={iDag} />
          </div>
        </div>
      </>
    );
  } else {
    innhold = <p className="hb-dempet">{`${navnAv(skriver)} kan sende én notis til de ${n} som meldte fra.`}</p>;
  }

  return (
    <section className="hb-sek hb-notis" aria-labelledby={tittelId} data-testid="hb-notis">
      <div className="hb-sek-hode">
        <Tittel id={tittelId} className="hb-sek-tittel">
          {tittel}
        </Tittel>
        {kant}
      </div>
      {innhold}
    </section>
  );
}
